import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Trash2 } from "lucide-react";
import { TspModal } from "../../../components/primitives";
import { formatDate } from "../../../utils/tspFormat";
import { isVideo } from "../mediaModel";
import MediaThumb from "./MediaThumb";

// Full-size view of one item with previous/next through the items currently
// loaded in the gallery (ArrowLeft / ArrowRight also work), its details and
// links to the related journey and city knowledge.
// `showDetails={false}`: only the photo / video with previous/next — for lists
// whose items carry no journey / city / upload details (e.g. the stats pages).
export default function MediaViewerModal({ items, index, onIndexChange, onClose, canRemove, canOpenKnowledge, onRemove, showDetails = true }) {
  const item = items[index];
  const hasPrev = index > 0;
  const hasNext = index < items.length - 1;

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.target.closest?.("input, select, textarea, video")) return;
      if (event.key === "ArrowLeft" && hasPrev) onIndexChange(index - 1);
      if (event.key === "ArrowRight" && hasNext) onIndexChange(index + 1);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [index, hasPrev, hasNext, onIndexChange]);

  if (!item) return null;
  const removable = canRemove && item.can_delete === true;

  return (
    <TspModal
      open
      size="lg"
      title={item.caption}
      onClose={onClose}
      footer={
        <>
          {removable && (
            <button type="button" className="tsp-btn tsp-btn-outline-primary tsp-viewer-remove" onClick={() => onRemove(item)}>
              <Trash2 size={14} /> Remove
            </button>
          )}
          <button type="button" className="tsp-btn tsp-btn-outline" onClick={() => onIndexChange(index - 1)} disabled={!hasPrev}>
            <ChevronLeft size={14} /> Previous
          </button>
          <span className="tsp-viewer-count" aria-live="polite">
            {index + 1} of {items.length}
          </span>
          <button type="button" className="tsp-btn tsp-btn-outline" onClick={() => onIndexChange(index + 1)} disabled={!hasNext}>
            Next <ChevronRight size={14} />
          </button>
        </>
      }
    >
      <div className="tsp-viewer-stage">
        <MediaThumb item={item} large />
      </div>
      {showDetails && (
      <dl className="tsp-viewer-meta">
        <div>
          <dt>Type</dt>
          <dd>{isVideo(item) ? "Video" : "Photo"}</dd>
        </div>
        <div>
          <dt>Journey</dt>
          <dd>{item.journey ? <Link to={`/tsp/journeys/${item.journey.id}`}>{item.journey.name}</Link> : "Another team's journey"}</dd>
        </div>
        <div>
          <dt>City</dt>
          <dd>{canOpenKnowledge && item.location ? <Link to={`/tsp/knowledge/${item.location.id}`}>{item.location.label}</Link> : item.location?.label || "—"}</dd>
        </div>
        <div>
          <dt>Uploaded</dt>
          <dd>
            {formatDate(item.created_at.slice(0, 10))}
            {item.uploaded_by ? ` by ${item.uploaded_by.name}` : ""}
          </dd>
        </div>
      </dl>
      )}
    </TspModal>
  );
}
